import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useCallback } from 'preact/hooks';
import { LogOut, Loader2 } from 'lucide-preact';
import { oneDriveAuthApi } from '@/api/oneDriveAuth';
import { OneDriveIcon } from '@/components/common/icons/OneDriveIcon';
const ONEDRIVE_PICKER_BASE_URL = 'https://onedrive.live.com/picker';
function buildPickerOptions(channelId) {
    return {
        sdk: '8.0',
        entry: { oneDrive: { files: {} } },
        selection: { mode: 'single' },
        typesAndSources: { mode: 'files' },
        messaging: {
            origin: window.location.origin,
            channelId,
        },
    };
}
function openPickerPopup(options, accessToken) {
    const width = 1080;
    const height = 680;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const pickerWindow = window.open('', 'onedrive-picker', `width=${width},height=${height},left=${left},top=${top},popup=yes`);
    if (!pickerWindow)
        return null;
    const queryString = new URLSearchParams({
        filePicker: JSON.stringify(options),
    });
    const url = `${ONEDRIVE_PICKER_BASE_URL}?${queryString}`;
    const form = pickerWindow.document.createElement('form');
    form.setAttribute('action', url);
    form.setAttribute('method', 'POST');
    const tokenInput = pickerWindow.document.createElement('input');
    tokenInput.setAttribute('type', 'hidden');
    tokenInput.setAttribute('name', 'access_token');
    tokenInput.setAttribute('value', accessToken);
    form.appendChild(tokenInput);
    pickerWindow.document.body.appendChild(form);
    form.submit();
    return pickerWindow;
}
async function fetchGraphFileMetadata(driveId, itemId, accessToken) {
    const graphUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}?select=name,file`;
    try {
        const res = await fetch(graphUrl, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        const meta = await res.json();
        const fileName = meta.name || '';
        return {
            fileId: itemId,
            fileName,
            mimeType: meta.file?.mimeType || mimeTypeFromExtension(fileName),
        };
    }
    catch {
        return { fileId: itemId, fileName: itemId, mimeType: 'application/octet-stream' };
    }
}
function handleAuthenticate(port, messageId) {
    oneDriveAuthApi.getPickerToken().then(tokenResp => {
        const token = tokenResp.data?.accessToken;
        port.postMessage({
            type: 'result',
            id: messageId,
            data: token
                ? { result: 'token', token }
                : { result: 'error', error: { code: 'tokenError', message: 'No token' } },
        });
    });
}
function handlePick(command, port, messageId, accessToken, pickerWindow, callbacks) {
    const items = command.items;
    if (items && items.length > 0) {
        const item = items[0];
        const driveId = item.parentReference?.driveId;
        if (driveId) {
            fetchGraphFileMetadata(driveId, item.id, accessToken).then(file => {
                callbacks.setPickedFile(file);
                callbacks.onFilePicked(file);
            });
        }
        else {
            const file = {
                fileId: item.id,
                fileName: item.name || item.id,
                mimeType: item.file?.mimeType || 'application/octet-stream',
            };
            callbacks.setPickedFile(file);
            callbacks.onFilePicked(file);
        }
    }
    port.postMessage({ type: 'result', id: messageId, data: { result: 'success' } });
    pickerWindow.close();
    callbacks.setIsPicking(false);
    port.close();
}
function createPortMessageHandler(getPort, accessToken, pickerWindow, callbacks) {
    return (event) => {
        const message = event.data;
        if (message.type === 'notification')
            return;
        if (message.type === 'command') {
            const port = getPort();
            port.postMessage({ type: 'acknowledge', id: message.id });
            if (message.data.command === 'authenticate') {
                handleAuthenticate(port, message.id);
            }
            else if (message.data.command === 'pick') {
                handlePick(message.data, port, message.id, accessToken, pickerWindow, callbacks);
            }
            else if (message.data.command === 'close') {
                pickerWindow.close();
                callbacks.setIsPicking(false);
                port.close();
            }
        }
    };
}
function pollPickerWindow(pickerWindow, onClosed) {
    const pollTimer = setInterval(() => {
        if (pickerWindow.closed) {
            clearInterval(pollTimer);
            onClosed();
        }
    }, 500);
}
export function OneDrivePicker({ auth, onFilePicked, onError }) {
    const { isConnected, providerEmail, isLoading, isConnecting, connect, disconnect } = auth;
    const [isPicking, setIsPicking] = useState(false);
    const [pickedFile, setPickedFile] = useState(null);
    const openPicker = useCallback(async () => {
        setIsPicking(true);
        try {
            const tokenResponse = await oneDriveAuthApi.getPickerToken();
            if (!tokenResponse.data) {
                onError?.('Failed to get OneDrive access. Please reconnect your account.');
                await disconnect();
                setIsPicking(false);
                return;
            }
            const { accessToken } = tokenResponse.data;
            const channelId = crypto.randomUUID();
            const options = buildPickerOptions(channelId);
            const pickerWindow = openPickerPopup(options, accessToken);
            if (!pickerWindow) {
                onError?.('Popup was blocked. Please allow popups and try again.');
                setIsPicking(false);
                return;
            }
            let port = null;
            const callbacks = { onFilePicked, setPickedFile, setIsPicking };
            const onPortMessage = createPortMessageHandler(() => port, accessToken, pickerWindow, callbacks);
            const handleWindowMessage = (event) => {
                if (event.source !== pickerWindow)
                    return;
                const message = event.data;
                if (message.type === 'initialize' && message.channelId === channelId) {
                    port = event.ports[0];
                    port.addEventListener('message', onPortMessage);
                    port.start();
                    port.postMessage({ type: 'activate' });
                    window.removeEventListener('message', handleWindowMessage);
                }
            };
            window.addEventListener('message', handleWindowMessage);
            pollPickerWindow(pickerWindow, () => {
                window.removeEventListener('message', handleWindowMessage);
                port?.close();
                setIsPicking(false);
            });
        }
        catch (error) {
            console.error('Failed to open OneDrive Picker:', error);
            onError?.('Failed to open OneDrive file picker. Please try again.');
            setIsPicking(false);
        }
    }, [onFilePicked, onError, disconnect]);
    if (isLoading) {
        return (_jsxs("div", { className: "flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4", children: [_jsx(Loader2, { className: "w-4 h-4 animate-spin" }), "Checking OneDrive connection..."] }));
    }
    if (!isConnected) {
        return (_jsx("div", { className: "flex justify-center py-6", children: _jsx("button", { type: "button", onClick: () => { onError?.(undefined); connect(); }, disabled: isConnecting, className: "inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50", children: isConnecting ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-4 h-4 animate-spin" }), "Connecting..."] })) : (_jsxs(_Fragment, { children: [_jsx(OneDriveIcon, { className: "w-5 h-5" }), "Sign in with OneDrive"] })) }) }));
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "flex items-center justify-between text-sm", children: [_jsxs("span", { className: "text-slate-500 dark:text-slate-400", children: ["Connected as ", _jsx("span", { className: "font-medium text-slate-700 dark:text-slate-200", children: providerEmail })] }), _jsxs("button", { type: "button", onClick: disconnect, className: "inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors", children: [_jsx(LogOut, { className: "w-3 h-3" }), "Disconnect"] })] }), pickedFile ? (_jsxs("div", { className: "flex items-center gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20", children: [_jsx(OneDriveIcon, { className: "w-5 h-5 flex-shrink-0" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-sm font-medium text-slate-900 dark:text-slate-100 truncate", children: pickedFile.fileName }), _jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: pickedFile.mimeType })] }), _jsx("button", { type: "button", onClick: () => {
                            setPickedFile(null);
                            openPicker();
                        }, className: "text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0", children: "Change" })] })) : (_jsx("button", { type: "button", onClick: openPicker, disabled: isPicking, className: "w-full flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 bg-slate-50 dark:bg-slate-700/30 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50", children: isPicking ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-4 h-4 animate-spin" }), "Opening OneDrive..."] })) : (_jsxs(_Fragment, { children: [_jsx(OneDriveIcon, { className: "w-5 h-5" }), "Pick from OneDrive"] })) }))] }));
}
const EXTENSION_MIME_MAP = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    zip: 'application/zip',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    xml: 'application/xml',
    html: 'text/html',
};
function mimeTypeFromExtension(fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return EXTENSION_MIME_MAP[ext] || 'application/octet-stream';
}
